import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Send, CheckCircle, ChevronDown, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSiteContent } from '../context/SiteContentContext';
import { useAnalytics } from '../context/AnalyticsContext';
import { toast } from 'sonner';
import axios from 'axios';
import { LANGUAGE_CODES } from '../constants/languages';
import { COUNTRY_CODES, LANGUAGE_TO_ISO2 } from '../constants/countryCodes';
import PhoneNumberField, { composePhoneValue, validatePhoneNational } from '../components/common/PhoneNumberField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AppIcon = ({ icon }) => {
  if (!icon) return <MessageCircle className="w-4 h-4 text-slate-500" />;
  const isImage = icon.startsWith('http') || icon.startsWith('/');
  if (isImage) return <img src={icon} alt="" className="w-4 h-4 object-contain rounded" />;
  return <span className="text-base leading-none">{icon}</span>;
};

const Contact = () => {
  const { siteContent } = useSiteContent();
  const { trackConversion, getUTMParams } = useAnalytics();
  const contactPage = siteContent?.contactPage || {};

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    preferredLanguage: '',
    message: '',
    voucherCode: ''
  });
  const [messagingIds, setMessagingIds] = useState({}); // { appId: value }
  const [messagingErrors, setMessagingErrors] = useState({}); // { appId: errorText }
  const [phoneParts, setPhoneParts] = useState({}); // { appId: { iso2, national } }
  const [phoneTouched, setPhoneTouched] = useState({}); // { appId: true } once user manually picks a country
  const [messengerMode, setMessengerMode] = useState({}); // { appId: 'username' | 'phone' }
  const [regionIso2, setRegionIso2] = useState(null); // IP-detected country, overrides language default
  const [dynamicApps, setDynamicApps] = useState([]);
  const [registeredLanguages, setRegisteredLanguages] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [messageError, setMessageError] = useState(null);

  // Simple name validation: required, 2-100 chars, letters (incl. Unicode),
  // spaces, hyphens, apostrophes, and dots (e.g. "St. John", "Jean-Luc").
  const validateName = (value) => {
    const v = (value || '').trim();
    if (!v) return 'Please enter your name.';
    if (v.length < 2) return 'Name must be at least 2 characters.';
    if (v.length > 100) return 'Name must be at most 100 characters.';
    if (!/^[\p{L}\p{M}\s'.\-]+$/u.test(v)) {
      return 'Name can only contain letters, spaces, hyphens, apostrophes and dots.';
    }
    return null;
  };

  // Required message, capped at 2000 chars (matches the textarea's maxLength).
  const validateMessage = (value) => {
    const v = (value || '').trim();
    if (!v) return 'Please enter a message.';
    if (v.length > 2000) return 'Message must be at most 2000 characters.';
    return null;
  };

  // Simple email validation: required + RFC-ish regex + length cap.
  const validateEmail = (value) => {
    const v = (value || '').trim();
    if (!v) return 'Please enter your email address.';
    if (v.length > 254) return 'Email is too long.';
    // Standard practical email regex (single @, no spaces, valid TLD).
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) {
      return 'Please enter a valid email address (e.g. name@example.com).';
    }
    return null;
  };

  // Client-side mirror of the backend's validate_messaging_id() rules so users get
  // instant feedback. Phone-type apps mandate a leading '+' (country code, chosen
  // from the dropdown) — this is what blocks numbers with no country code.
  const validateUsernameValue = (v, app) => {
    const prefixes = app.allowed_prefixes || [];
    let stripped = v;
    for (const p of prefixes) {
      if (p && stripped.startsWith(p)) { stripped = stripped.slice(p.length); break; }
    }
    const chars = app.allowed_chars || 'any';
    if (chars === 'digits' && !/^\d+$/.test(stripped)) {
      return app.error_message || `${app.name} accepts digits only.`;
    }
    if (chars === 'alphanumeric' && !/^[A-Za-z0-9_.]+$/.test(stripped)) {
      return app.error_message || `${app.name} accepts letters, digits, dots and underscores only.`;
    }
    if (Number.isInteger(app.min_length) && stripped.length < app.min_length) {
      return app.error_message || `${app.name} must be at least ${app.min_length} characters.`;
    }
    if (Number.isInteger(app.max_length) && stripped.length > app.max_length) {
      return app.error_message || `${app.name} must be at most ${app.max_length} characters.`;
    }
    if (app.regex_pattern) {
      try {
        if (!new RegExp(`^(?:${app.regex_pattern})$`).test(stripped)) {
          return app.error_message || `${app.name} format is invalid.`;
        }
      } catch (e) { /* bad admin regex - don't block */ }
    }
    return null;
  };

  const validatePhoneValue = (v, app) => {
    if (!v.startsWith('+')) {
      return app.error_message || `${app.name} needs a country code — please select one from the dropdown.`;
    }
    const digits = v.slice(1);
    if (!digits || !/^\d+$/.test(digits)) {
      return app.error_message || `${app.name} number must contain digits only after the country code.`;
    }
    const min = Number.isInteger(app.min_length) ? app.min_length : 8;
    const max = Number.isInteger(app.max_length) ? app.max_length : 15;
    if (digits.length < min || digits.length > max) {
      return app.error_message || `${app.name} number length looks invalid.`;
    }
    // Note: regex_pattern is intentionally NOT applied for phone values — for
    // hybrid apps (Messenger) it holds the username pattern and would wrongly
    // reject valid international numbers.
    return null;
  };

  const validateMessagingId = (value, app) => {
    if (!value) return null;
    const v = value.trim();
    if (!v) return null;
    const inputType = app.input_type || 'username';
    if (inputType === 'phone') return validatePhoneValue(v, app);
    if (inputType === 'phone_or_username') {
      return (v[0] === '+' || /\d/.test(v[0])) ? validatePhoneValue(v, app) : validateUsernameValue(v, app);
    }
    return validateUsernameValue(v, app);
  };

  const handleMessagingIdChange = (app, value) => {
    setMessagingIds(prev => ({ ...prev, [app.id]: value }));
    setMessagingErrors(prev => ({ ...prev, [app.id]: validateMessagingId(value, app) }));
  };

  // IP-region takes priority over the language-based default whenever known;
  // falls back to the communication language, then the site's home country.
  const getDefaultIso2 = () => {
    if (regionIso2 && COUNTRY_CODES.some(c => c.iso2 === regionIso2)) return regionIso2;
    return LANGUAGE_TO_ISO2[formData.preferredLanguage] || 'PH';
  };

  const handlePhoneCountryChange = (app, iso2) => {
    setPhoneTouched(prev => ({ ...prev, [app.id]: true }));
    const national = phoneParts[app.id]?.national || '';
    setPhoneParts(prev => ({ ...prev, [app.id]: { iso2, national } }));
    const composed = composePhoneValue(iso2, national);
    setMessagingIds(prev => ({ ...prev, [app.id]: composed }));
    const nsnErr = national ? validatePhoneNational(iso2, national, app.name) : null;
    setMessagingErrors(prev => ({ ...prev, [app.id]: nsnErr || validateMessagingId(composed, app) }));
  };

  const handlePhoneNationalChange = (app, national) => {
    const iso2 = phoneParts[app.id]?.iso2 || getDefaultIso2();
    setPhoneParts(prev => ({ ...prev, [app.id]: { iso2, national } }));
    const composed = composePhoneValue(iso2, national);
    setMessagingIds(prev => ({ ...prev, [app.id]: composed }));
    const nsnErr = national ? validatePhoneNational(iso2, national, app.name) : null;
    setMessagingErrors(prev => ({ ...prev, [app.id]: nsnErr || validateMessagingId(composed, app) }));
  };

  const handleMessengerModeToggle = (app, mode) => {
    setMessengerMode(prev => ({ ...prev, [app.id]: mode }));
    setMessagingIds(prev => ({ ...prev, [app.id]: '' }));
    setMessagingErrors(prev => ({ ...prev, [app.id]: null }));
  };

  // Best-effort IP geolocation for a sensible default country + example number.
  // Silently falls back to the language-based default on any failure.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/geo`);
        if (!cancelled && res.data?.country) setRegionIso2(res.data.country);
      } catch (err) { /* fall back to language-based default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Seed each phone-capable app's default country whenever the region is
  // detected, the language changes, or the app list changes — but never
  // override a country the user already picked manually.
  useEffect(() => {
    const def = getDefaultIso2();
    setPhoneParts(prev => {
      const next = { ...prev };
      dynamicApps.forEach(app => {
        if ((app.input_type === 'phone' || app.input_type === 'phone_or_username') && !phoneTouched[app.id]) {
          next[app.id] = { iso2: def, national: prev[app.id]?.national || '' };
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionIso2, formData.preferredLanguage, dynamicApps]);

  // Fetch the site's registered languages so the preferred-communication-language
  // dropdown always matches what's in the CMS (no more hardcoded 3-lang list).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/languages`);
        if (!cancelled) setRegisteredLanguages(res.data.languages || []);
      } catch (err) {
        if (!cancelled) setRegisteredLanguages([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch messaging apps for the language the user has chosen in the "Preferred
  // communication language" dropdown. Not the site language. No fallback to
  // English or the site language — if nothing is selected, no apps are shown.
  useEffect(() => {
    let cancelled = false;
    const chosen = formData.preferredLanguage;
    if (!chosen) {
      setDynamicApps([]);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/messaging-apps?lang=${encodeURIComponent(chosen)}`);
        if (!cancelled) setDynamicApps(res.data.apps || []);
      } catch (err) {
        if (!cancelled) setDynamicApps([]);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.preferredLanguage]);

  // Prune stale per-app state when the visible app list changes (e.g. after
  // switching preferred language) so values from a no-longer-shown app are
  // never silently submitted.
  useEffect(() => {
    const validIds = new Set(dynamicApps.map(a => a.id));
    const prune = (setter) => setter(prev => {
      const next = {};
      let changed = false;
      for (const [id, val] of Object.entries(prev)) {
        if (validIds.has(id)) next[id] = val;
        else changed = true;
      }
      return changed ? next : prev;
    });
    prune(setMessagingIds);
    prune(setMessagingErrors);
    prune(setPhoneParts);
    prune(setPhoneTouched);
    prune(setMessengerMode);
  }, [dynamicApps]);

  // Build dropdown ONLY from the languages registered in the CMS. We deliberately
  // ignore the old `contactPage.formLanguageOptions` override — that legacy field
  // used name-based values ("japanese", "korean") which don't match the API's
  // code-based filter and could silently disable messaging-app mapping.
  const languageOptions = registeredLanguages.map(l => {
    const engName = (l.name || l.code || '').toString();
    const nativeName = (l.nativeName || '').toString();
    const label = nativeName && nativeName !== engName
      ? `${nativeName} (${engName})`
      : engName;
    return { value: (l.code || engName).toString().toLowerCase(), label };
  });

  const locations = contactPage.locations || ['Cebu, Philippines', 'London, UK'];
  
  // Form placeholders and UI text
  const formNamePlaceholder = contactPage.formNamePlaceholder || 'Your name';
  const formEmailPlaceholder = contactPage.formEmailPlaceholder || 'your@email.com';
  const formLanguagePlaceholder = contactPage.formLanguagePlaceholder || 'Select your preferred language';
  const formMessagePlaceholder = contactPage.formMessagePlaceholder || 'Tell us how we can help...';
  const optionalText = contactPage.optionalText || 'optional';
  const sendingText = contactPage.sendingText || 'Sending...';

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate name + email + message up front
    const nErr = validateName(formData.name);
    const eErr = validateEmail(formData.email);
    const mErr = validateMessage(formData.message);
    setNameError(nErr);
    setEmailError(eErr);
    setMessageError(mErr);
    // Re-run validation on all filled messaging IDs; block submit if any fail
    const errors = {};
    for (const app of dynamicApps) {
      const value = messagingIds[app.id] || '';
      let err = null;
      if ((app.input_type === 'phone' || app.input_type === 'phone_or_username') && value.startsWith('+')) {
        const parts = phoneParts[app.id];
        if (parts?.national) err = validatePhoneNational(parts.iso2, parts.national, app.name);
      }
      if (!err) err = validateMessagingId(value, app);
      if (err) errors[app.id] = err;
    }
    setMessagingErrors(errors);
    if (nErr || eErr || mErr || Object.keys(errors).length > 0) {
      toast.error('Please fix the highlighted errors before sending.');
      return;
    }
    setLoading(true);
    
    const utmParams = getUTMParams();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          preferred_language: formData.preferredLanguage,
          messaging_ids: Object.fromEntries(Object.entries(messagingIds).filter(([, v]) => (v || '').trim())),
          message: formData.message,
          voucher_code: formData.voucherCode?.trim() || null,
          utm_source: utmParams.utm_source || null,
          utm_medium: utmParams.utm_medium || null,
          utm_campaign: utmParams.utm_campaign || null
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        trackConversion('contact_form_submit', {
          preferred_language: formData.preferredLanguage || 'not_specified',
          routed_to: result.routed_to,
          messaging_apps_filled: Object.values(messagingIds).filter(v => (v || '').trim()).length
        });
        setSubmitted(true);
      } else {
        const errorData = await response.json().catch(() => null);
        if (errorData?.detail) {
          if (Array.isArray(errorData.detail)) {
            const firstError = errorData.detail[0];
            const fieldName = firstError.loc?.[1] || 'field';
            throw new Error(`Please check your ${fieldName}: ${firstError.msg}`);
          }
          throw new Error(errorData.detail);
        }
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4 block">
              {contactPage.headerLabel || 'Get in Touch'}
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
              {contactPage.headerTitle || 'Contact Us'}
            </h1>
            <p className="text-xl text-slate-600">
              {contactPage.headerSubtitle || "Have questions or ready to start? We'd love to hear from you."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">
                {contactPage.formTitle || 'Send us a message'}
              </h2>
              
              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{contactPage.successTitle || 'Message Sent!'}</h3>
                  <p className="text-slate-600">{contactPage.successText || "Thank you for reaching out. We'll get back to you soon."}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {contactPage.formNameLabel || 'Name'}
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, name: val });
                        if (nameError) setNameError(validateName(val));
                      }}
                      onBlur={(e) => setNameError(validateName(e.target.value))}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                        nameError
                          ? 'border-red-400 focus:ring-red-400'
                          : 'border-slate-200 focus:ring-slate-900'
                      }`}
                      placeholder={formNamePlaceholder}
                      data-testid="contact-name-input"
                      aria-invalid={!!nameError}
                    />
                    {nameError && (
                      <p className="text-xs text-red-600 mt-2" data-testid="contact-name-error">{nameError}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {contactPage.formEmailLabel || 'Email'}
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, email: val });
                        if (emailError) setEmailError(validateEmail(val));
                      }}
                      onBlur={(e) => setEmailError(validateEmail(e.target.value))}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                        emailError
                          ? 'border-red-400 focus:ring-red-400'
                          : 'border-slate-200 focus:ring-slate-900'
                      }`}
                      placeholder={formEmailPlaceholder}
                      data-testid="contact-email-input"
                      aria-invalid={!!emailError}
                    />
                    {emailError && (
                      <p className="text-xs text-red-600 mt-2" data-testid="contact-email-error">{emailError}</p>
                    )}
                  </div>

                  {/* Preferred communication language */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {contactPage.formLanguageLabel || 'Preferred communication language'}
                      <span className="text-slate-400 font-normal ml-1">({optionalText})</span>
                    </label>
                    <div className="relative">
                      <select
                        value={formData.preferredLanguage}
                        onChange={(e) => setFormData({ ...formData, preferredLanguage: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all appearance-none bg-white cursor-pointer text-slate-700"
                        data-testid="contact-language-select"
                      >
                        <option value="">{formLanguagePlaceholder}</option>
                        {languageOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                    </div>
                    <p className="text-xs text-slate-500 mt-2" data-testid="contact-language-helper">
                      {contactPage.formLanguageHelperText || 'We will reply to you in this language.'}
                    </p>
                  </div>

                  {/* Dynamic messaging apps mapped to the current site language */}
                  {dynamicApps.map(app => {
                    const err = messagingErrors[app.id];

                    // Phone-only apps: WhatsApp, Viber, Signal, Zalo — mandatory country code
                    if (app.input_type === 'phone') {
                      const parts = phoneParts[app.id] || { iso2: getDefaultIso2(), national: '' };
                      return (
                        <div key={app.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid={`contact-app-${app.id}`}>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            <span className="flex items-center gap-2">
                              <AppIcon icon={app.icon} />
                              {app.name}
                              <span className="text-slate-400 font-normal">({optionalText})</span>
                            </span>
                          </label>
                          <PhoneNumberField
                            iso2={parts.iso2}
                            national={parts.national}
                            onChangeIso2={(iso2) => handlePhoneCountryChange(app, iso2)}
                            onChangeNational={(national) => handlePhoneNationalChange(app, national)}
                            error={err}
                            testId={`contact-app-input-${app.id}`}
                          />
                          {err ? (
                            <p className="text-xs text-red-600 mt-2" data-testid={`contact-app-error-${app.id}`}>{err}</p>
                          ) : app.description ? (
                            <p className="text-xs text-slate-500 mt-2">{app.description}</p>
                          ) : null}
                        </div>
                      );
                    }

                    // Hybrid apps: Messenger — username OR phone, toggled by the user
                    if (app.input_type === 'phone_or_username') {
                      const mode = messengerMode[app.id] || 'username';
                      const parts = phoneParts[app.id] || { iso2: getDefaultIso2(), national: '' };
                      return (
                        <div key={app.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid={`contact-app-${app.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-slate-700">
                              <span className="flex items-center gap-2">
                                <AppIcon icon={app.icon} />
                                {app.name}
                                <span className="text-slate-400 font-normal">({optionalText})</span>
                              </span>
                            </label>
                            <div className="flex bg-white border border-slate-200 rounded-lg overflow-hidden text-xs" data-testid={`contact-app-mode-${app.id}`}>
                              <button
                                type="button"
                                onClick={() => handleMessengerModeToggle(app, 'username')}
                                data-testid={`contact-app-mode-username-${app.id}`}
                                className={`px-3 py-1.5 font-medium transition-colors ${mode === 'username' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}
                              >
                                Username
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMessengerModeToggle(app, 'phone')}
                                data-testid={`contact-app-mode-phone-${app.id}`}
                                className={`px-3 py-1.5 font-medium transition-colors ${mode === 'phone' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}
                              >
                                Phone
                              </button>
                            </div>
                          </div>
                          {mode === 'phone' ? (
                            <PhoneNumberField
                              iso2={parts.iso2}
                              national={parts.national}
                              onChangeIso2={(iso2) => handlePhoneCountryChange(app, iso2)}
                              onChangeNational={(national) => handlePhoneNationalChange(app, national)}
                              error={err}
                              testId={`contact-app-input-${app.id}`}
                            />
                          ) : (
                            <input
                              type="text"
                              value={messagingIds[app.id] || ''}
                              onChange={(e) => handleMessagingIdChange(app, e.target.value)}
                              onBlur={(e) => handleMessagingIdChange(app, e.target.value)}
                              data-testid={`contact-app-input-${app.id}`}
                              placeholder={`Your ${app.name} username`}
                              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-white ${
                                err ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:ring-slate-900'
                              }`}
                              aria-invalid={!!err}
                            />
                          )}
                          {err ? (
                            <p className="text-xs text-red-600 mt-2" data-testid={`contact-app-error-${app.id}`}>{err}</p>
                          ) : app.description ? (
                            <p className="text-xs text-slate-500 mt-2">{app.description}</p>
                          ) : null}
                        </div>
                      );
                    }

                    // Username / handle-style apps: LINE, KakaoTalk, Telegram, custom apps
                    const hint = [];
                    if (app.allowed_prefixes?.length) hint.push(`starts with ${app.allowed_prefixes.join(' or ')}`);
                    if (app.min_length && app.max_length) hint.push(`${app.min_length}-${app.max_length} chars`);
                    else if (app.min_length) hint.push(`≥${app.min_length} chars`);
                    else if (app.max_length) hint.push(`≤${app.max_length} chars`);
                    const hintText = hint.join(' · ');
                    return (
                      <div key={app.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid={`contact-app-${app.id}`}>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          <span className="flex items-center gap-2">
                            <AppIcon icon={app.icon} />
                            {app.name} ID
                            <span className="text-slate-400 font-normal">({optionalText})</span>
                          </span>
                        </label>
                        <input
                          type="text"
                          value={messagingIds[app.id] || ''}
                          onChange={(e) => handleMessagingIdChange(app, e.target.value)}
                          onBlur={(e) => handleMessagingIdChange(app, e.target.value)}
                          data-testid={`contact-app-input-${app.id}`}
                          placeholder={`Your ${app.name} ID`}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-white ${
                            err
                              ? 'border-red-400 focus:ring-red-400'
                              : 'border-slate-200 focus:ring-slate-900'
                          }`}
                          aria-invalid={!!err}
                        />
                        {err ? (
                          <p className="text-xs text-red-600 mt-2" data-testid={`contact-app-error-${app.id}`}>{err}</p>
                        ) : (
                          <p className="text-xs text-slate-500 mt-2">
                            {app.description}
                            {app.description && hintText ? ' · ' : ''}
                            {hintText}
                          </p>
                        )}
                      </div>
                    );
                  })}

                  {/* Voucher / Referral code (optional) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {contactPage.formVoucherLabel || 'Voucher / Referral Code'}
                      <span className="text-slate-400 font-normal ml-1">({optionalText})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.voucherCode}
                      onChange={(e) => setFormData({ ...formData, voucherCode: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                      placeholder={contactPage.formVoucherPlaceholder || 'Have a code from a referral? Enter it here'}
                      data-testid="contact-voucher-input"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {contactPage.formMessageLabel || 'Message'}
                    </label>
                    <textarea
                      rows={5}
                      maxLength={2000}
                      value={formData.message}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, message: val });
                        if (messageError) setMessageError(validateMessage(val));
                      }}
                      onBlur={(e) => setMessageError(validateMessage(e.target.value))}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent resize-none transition-all ${
                        messageError ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:ring-slate-900'
                      }`}
                      placeholder={formMessagePlaceholder}
                      data-testid="contact-message-input"
                      aria-invalid={!!messageError}
                    />
                    {messageError && (
                      <p className="text-xs text-red-600 mt-2" data-testid="contact-message-error">{messageError}</p>
                    )}
                    {(() => {
                      const len = formData.message.length;
                      const max = 2000;
                      const remaining = max - len;
                      const nearLimit = remaining <= 100;
                      const atLimit = remaining <= 0;
                      return (
                        <div
                          className={`text-xs mt-2 text-right ${
                            atLimit ? 'text-red-600' : nearLimit ? 'text-amber-600' : 'text-slate-500'
                          }`}
                          data-testid="contact-message-counter"
                          aria-live="polite"
                        >
                          {len} / {max} {contactPage.formMessageCounterLabel || 'characters'}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-4 rounded-xl font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
                    data-testid="contact-submit-button"
                  >
                    {loading ? sendingText : (
                      <>
                        <Send className="w-4 h-4" />
                        {contactPage.formSubmitButton || 'Send Message'}
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">
                {contactPage.infoTitle || 'Contact Information'}
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-slate-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">{contactPage.contactInfoEmailLabel || 'Email'}</h3>
                    <a href={`mailto:${contactPage.email || 'contact@englishpassion.uk'}`} className="text-slate-600 hover:text-slate-900">
                      {contactPage.email || 'contact@englishpassion.uk'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-slate-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">{contactPage.contactInfoLocationsLabel || 'Locations'}</h3>
                    {locations.map((location, index) => (
                      <p key={`location-${location.slice(0, 10)}-${index}`} className="text-slate-600">{location}</p>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-slate-900 rounded-xl">
                <h3 className="font-semibold mb-2 text-white">{contactPage.ctaTitle || 'Ready to get started?'}</h3>
                <p className="text-slate-300 text-sm">
                  {contactPage.ctaText || "Fill out the form and we'll be in touch within 24 hours to schedule your free trial lesson."}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
